import classNames from 'classnames';
import React, { useMemo } from 'react';

import { Language } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { MemberPhone, Patient } from '@vestahealthcare/common/models';

import { Chip, PhoneLink } from 'styleguide-v2';

import { RowStyles } from './ContactInfoRowStyles';
import { MemberType } from './types';

type Props = {
  className?: string;
  editMode?: boolean;
  index?: number;
  member: MemberType;
};

export const ContactInfoMemberRow = ({
  className,
  editMode,
  index = 0,
  member,
}: Props) => {
  const styles = RowStyles();

  const userPhones = useMemo(
    () =>
      member.phones
        ?.filter(MemberPhone.filterActive)
        ?.sort(MemberPhone.sortByType),
    [member?.phones],
  );

  const renderLanguage = (lang?: Language) =>
    member && lang ? (
      <Chip
        color="info"
        key={`member-language-${lang.value}`}
        type={member.language === lang ? 'contained' : 'outlined'}
        size="small"
      >
        {lang.toString()}
      </Chip>
    ) : (
      <></>
    );

  return (
    <div
      className={classNames(
        className,
        !editMode && index % 2 === 1 && styles.rowEven,
        styles.row,
      )}
    >
      <div className={classNames('grid-wrapper rows-24', styles.rowInner)}>
        <span className="grid-span-4">{member.fullName}</span>
        <div className="grid-span-11">
          <Chip color="info" type="outlined" size="small">
            {translate('global.member')}
          </Chip>
        </div>
        <div className="grid-span-4 flex wrap gap end">
          {member instanceof Patient
            ? member?.languages?.map(renderLanguage)
            : renderLanguage(member.language)}
        </div>
        <div className="grid-span-5">
          {userPhones?.[0] && (
            <PhoneLink
              compact
              phone={userPhones[0].phone.number}
              type={userPhones[0].phone.type?.toString()}
              primary={userPhones[0].preferred}
              icon={
                userPhones[0].primary ? (
                  <span className="size-xs grey">
                    ({translate('global.primary')})
                  </span>
                ) : undefined
              }
            />
          )}
        </div>
      </div>
      {!editMode &&
        userPhones?.slice(1)?.map(({ phone, preferred, primary }) => (
          <div
            key={phone.number}
            className={classNames('grid-wrapper rows-24', styles.rowInner)}
          >
            <div className="grid-span-19" />
            <div className="grid-span-5">
              <PhoneLink
                compact
                phone={phone.number}
                type={phone.type?.toString()}
                primary={preferred}
                icon={
                  primary ? (
                    <span className="size-xs grey">
                      ({translate('global.primary')})
                    </span>
                  ) : undefined
                }
              />
            </div>
          </div>
        ))}
    </div>
  );
};
